import styled from 'styled-components';
import tw from 'twin.macro';
import {
  StayCancellationPolicy,
  StayCheckInMethod,
  StayPropertyType,
  StayRoomType,
  type StayAmenity,
} from '../../../__generated__/graphql';
import SwitchComponent from '../../components/switch';
import {
  AdornedInput,
  Field,
  FormGrid,
  Input,
  Muted,
  Select,
  Stack,
  Strong,
  Textarea,
} from '../../components/ui';
import {
  AMENITY_GROUPS,
  CANCELLATION_POLICY_LABELS,
  CHECK_IN_METHOD_LABELS,
  PROPERTY_TYPE_LABELS,
  ROOM_TYPE_LABELS,
  amenityLabel,
  type StayForm,
  type StayFormKey,
  type StaySectionId,
} from './stayForm';

export interface SectionProps {
  form: StayForm;
  errors: Partial<Record<StayFormKey, string>>;
  onChange: (patch: Partial<StayForm>) => void;
}

// ─── building blocks ───────────────────────────────────────────────────────

export const OptionGrid = styled.div<{ $cols?: 2 | 3 }>`
  ${tw`grid gap-3 grid-cols-2`}
  ${({ $cols = 3 }) => ($cols === 3 ? tw`sm:grid-cols-3` : tw`sm:grid-cols-2`)}
`;

export const OptionCard = styled.button<{ $selected?: boolean }>`
  ${tw`flex flex-col items-start gap-1 p-4 text-left cursor-pointer`}
  border-radius: var(--radius-card, 12px);
  background: ${({ $selected }) =>
    $selected ? 'var(--color-bg-secondary)' : 'var(--color-bg-elevated, #fff)'};
  border: ${({ $selected }) =>
    $selected ? '2px solid var(--color-text-primary)' : '1px solid var(--color-border)'};
  color: var(--color-text-primary);
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
  &:hover {
    border-color: var(--color-text-primary);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const OptionTitle = styled.span`
  ${tw`text-base font-semibold`}
`;

const OptionText = styled.span`
  ${tw`text-sm`}
  color: var(--color-text-secondary);
`;

const CounterRow = styled.div`
  ${tw`flex items-center justify-between py-4`}
  border-bottom: 1px solid var(--color-border);
  &:last-child {
    border-bottom: none;
  }
`;

const CounterControls = styled.div`
  ${tw`flex items-center gap-3`}
`;

const RoundButton = styled.button`
  ${tw`flex items-center justify-center w-8 h-8 text-lg leading-none cursor-pointer`}
  border-radius: 9999px;
  border: 1px solid var(--color-border-strong);
  background: var(--color-bg-elevated, #fff);
  color: var(--color-text-primary);
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const CounterValue = styled.span`
  ${tw`w-8 text-center text-base`}
  color: var(--color-text-primary);
`;

const ToggleRow = styled.div`
  ${tw`flex items-center justify-between gap-4 py-3`}
  border-bottom: 1px solid var(--color-border);
  &:last-child {
    border-bottom: none;
  }
`;

const GroupTitle = styled.h4`
  ${tw`mb-3 text-sm font-semibold`}
  color: var(--color-text-primary);
`;

function Counter({
  label,
  hint,
  value,
  min,
  max,
  step = 1,
  onChange,
  testId,
}: {
  label: string;
  hint?: string;
  value: string;
  min: number;
  max: number;
  step?: number;
  onChange: (value: string) => void;
  testId?: string;
}) {
  const current = Number(value) || 0;
  const set = (next: number) => onChange(String(Math.min(max, Math.max(min, next))));
  return (
    <CounterRow>
      <div>
        <Strong>{label}</Strong>
        {hint && (
          <div>
            <Muted $size="sm">{hint}</Muted>
          </div>
        )}
      </div>
      <CounterControls>
        <RoundButton
          type="button"
          aria-label={`Fewer ${label.toLowerCase()}`}
          disabled={current <= min}
          onClick={() => set(current - step)}
        >
          −
        </RoundButton>
        <CounterValue data-testid={testId}>{current}</CounterValue>
        <RoundButton
          type="button"
          aria-label={`More ${label.toLowerCase()}`}
          disabled={current >= max}
          onClick={() => set(current + step)}
        >
          +
        </RoundButton>
      </CounterControls>
    </CounterRow>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
  testId,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  testId?: string;
}) {
  return (
    <ToggleRow>
      <div>
        <Strong>{label}</Strong>
        {hint && (
          <div>
            <Muted $size="sm">{hint}</Muted>
          </div>
        )}
      </div>
      <SwitchComponent
        isActive={checked}
        onChange={() => onChange(!checked)}
        inputTestId={testId}
        labelTestId={testId ? `${testId}-label` : undefined}
      />
    </ToggleRow>
  );
}

const money = (props: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => (
  <AdornedInput
    id={props.id}
    leading="$"
    inputMode="decimal"
    value={props.value}
    disabled={props.disabled}
    onChange={event => props.onChange(event.target.value)}
  />
);

const percent = (props: { id: string; value: string; onChange: (value: string) => void }) => (
  <AdornedInput
    id={props.id}
    trailing="%"
    inputMode="decimal"
    value={props.value}
    onChange={event => props.onChange(event.target.value)}
  />
);

// ─── sections ──────────────────────────────────────────────────────────────

export function PropertyTypeSection({ form, onChange }: SectionProps) {
  return (
    <OptionGrid>
      {Object.values(StayPropertyType).map(type => (
        <OptionCard
          key={type}
          type="button"
          $selected={form.propertyType === type}
          aria-pressed={form.propertyType === type}
          onClick={() => onChange({ propertyType: type })}
          data-testid={`property-type-${type}`}
        >
          <OptionTitle>{PROPERTY_TYPE_LABELS[type]}</OptionTitle>
        </OptionCard>
      ))}
    </OptionGrid>
  );
}

export function RoomTypeSection({ form, onChange }: SectionProps) {
  return (
    <Stack $gap={4}>
      {Object.values(StayRoomType).map(type => (
        <OptionCard
          key={type}
          type="button"
          $selected={form.roomType === type}
          aria-pressed={form.roomType === type}
          onClick={() => onChange({ roomType: type })}
          data-testid={`room-type-${type}`}
        >
          <OptionTitle>{ROOM_TYPE_LABELS[type].title}</OptionTitle>
          <OptionText>{ROOM_TYPE_LABELS[type].description}</OptionText>
        </OptionCard>
      ))}
    </Stack>
  );
}

export function BasicsSection(props: SectionProps & { withTypes?: boolean }) {
  const { form, onChange, errors, withTypes } = props;
  return (
    <Stack $gap={6}>
      {withTypes && (
        <FormGrid $cols={2}>
          <Field label="Property type" htmlFor="stay-property-type">
            <Select
              id="stay-property-type"
              value={form.propertyType}
              onChange={event => onChange({ propertyType: event.target.value as StayPropertyType })}
            >
              {Object.values(StayPropertyType).map(type => (
                <option key={type} value={type}>
                  {PROPERTY_TYPE_LABELS[type]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Guests have" htmlFor="stay-room-type">
            <Select
              id="stay-room-type"
              value={form.roomType}
              onChange={event => onChange({ roomType: event.target.value as StayRoomType })}
            >
              {Object.values(StayRoomType).map(type => (
                <option key={type} value={type}>
                  {ROOM_TYPE_LABELS[type].title}
                </option>
              ))}
            </Select>
          </Field>
        </FormGrid>
      )}
      <div>
        <Counter
          label="Guests"
          value={form.maxGuests}
          min={1}
          max={16}
          onChange={maxGuests => onChange({ maxGuests })}
          testId="counter-guests"
        />
        <Counter
          label="Bedrooms"
          value={form.bedrooms}
          min={0}
          max={50}
          onChange={bedrooms => onChange({ bedrooms })}
          testId="counter-bedrooms"
        />
        <Counter
          label="Beds"
          value={form.beds}
          min={1}
          max={50}
          onChange={beds => onChange({ beds })}
          testId="counter-beds"
        />
        <Counter
          label="Bathrooms"
          hint="Count a half bath as 0.5"
          value={form.bathrooms}
          min={0}
          max={50}
          step={0.5}
          onChange={bathrooms => onChange({ bathrooms })}
          testId="counter-bathrooms"
        />
      </div>
      {(errors.maxGuests || errors.beds || errors.bathrooms || errors.bedrooms) && (
        <Muted $size="sm" role="alert">
          {errors.maxGuests || errors.beds || errors.bathrooms || errors.bedrooms}
        </Muted>
      )}
    </Stack>
  );
}

export function AmenitiesSection({ form, onChange }: SectionProps) {
  const toggle = (amenity: StayAmenity) =>
    onChange({
      amenities: form.amenities.includes(amenity)
        ? form.amenities.filter(item => item !== amenity)
        : [...form.amenities, amenity],
    });
  return (
    <Stack $gap={6}>
      {AMENITY_GROUPS.map(group => (
        <div key={group.title}>
          <GroupTitle>{group.title}</GroupTitle>
          <OptionGrid>
            {group.items.map(amenity => (
              <OptionCard
                key={amenity}
                type="button"
                $selected={form.amenities.includes(amenity)}
                aria-pressed={form.amenities.includes(amenity)}
                onClick={() => toggle(amenity)}
                data-testid={`amenity-${amenity}`}
              >
                <OptionTitle>{amenityLabel(amenity)}</OptionTitle>
              </OptionCard>
            ))}
          </OptionGrid>
        </div>
      ))}
    </Stack>
  );
}

export function TitleSection({ form, onChange, errors }: SectionProps) {
  return (
    <Field
      label="Title"
      htmlFor="stay-title"
      error={errors.name}
      hint={`${form.name.length}/50`}
      required
    >
      <Input
        id="stay-title"
        maxLength={50}
        value={form.name}
        placeholder="Quiet cabin by the lake"
        onChange={event => onChange({ name: event.target.value })}
      />
    </Field>
  );
}

export function DescriptionSection({ form, onChange, errors }: SectionProps) {
  const length = form.description.trim().length;
  return (
    <Stack $gap={4}>
      <Field
        label="Description"
        htmlFor="stay-description"
        error={errors.description}
        hint={length < 50 ? `${50 - length} more characters to publish` : `${length}/5000`}
        required
      >
        <Textarea
          id="stay-description"
          rows={6}
          maxLength={5000}
          value={form.description}
          onChange={event => onChange({ description: event.target.value })}
        />
      </Field>
      <Field label="The space (optional)" htmlFor="stay-space">
        <Textarea
          id="stay-space"
          rows={3}
          maxLength={5000}
          value={form.descriptionSpace}
          onChange={event => onChange({ descriptionSpace: event.target.value })}
        />
      </Field>
      <Field label="Guest access (optional)" htmlFor="stay-access">
        <Textarea
          id="stay-access"
          rows={3}
          maxLength={5000}
          value={form.descriptionGuestAccess}
          onChange={event => onChange({ descriptionGuestAccess: event.target.value })}
        />
      </Field>
      <Field label="The neighborhood (optional)" htmlFor="stay-neighborhood">
        <Textarea
          id="stay-neighborhood"
          rows={3}
          maxLength={5000}
          value={form.descriptionNeighborhood}
          onChange={event => onChange({ descriptionNeighborhood: event.target.value })}
        />
      </Field>
    </Stack>
  );
}

export function BookingSection({ form, onChange, errors }: SectionProps) {
  return (
    <Stack $gap={6}>
      <div>
        <Toggle
          label="Instant Book"
          hint="Guests who meet your requirements book right away. Turn off to review each request within 24 hours."
          checked={form.instantBook}
          onChange={instantBook => onChange({ instantBook })}
          testId="toggle-instant-book"
        />
        <Toggle
          label="Require a verified ID for Instant Book"
          hint="Other guests can still send a request."
          checked={form.requireVerifiedId}
          onChange={requireVerifiedId => onChange({ requireVerifiedId })}
        />
      </div>
      <FormGrid $cols={2}>
        <Field label="Minimum nights" htmlFor="stay-min-nights" error={errors.minimumNights}>
          <Input
            id="stay-min-nights"
            inputMode="numeric"
            value={form.minimumNights}
            onChange={event => onChange({ minimumNights: event.target.value })}
          />
        </Field>
        <Field label="Maximum nights" htmlFor="stay-max-nights" error={errors.maximumNights}>
          <Input
            id="stay-max-nights"
            inputMode="numeric"
            value={form.maximumNights}
            onChange={event => onChange({ maximumNights: event.target.value })}
          />
        </Field>
        <Field
          label="Advance notice"
          htmlFor="stay-notice"
          error={errors.advanceNoticeHours}
          hint="How soon before check-in a guest can book"
        >
          <Select
            id="stay-notice"
            value={form.advanceNoticeHours}
            onChange={event => onChange({ advanceNoticeHours: event.target.value })}
          >
            <option value="0">Same day</option>
            <option value="24">At least 1 day</option>
            <option value="48">At least 2 days</option>
            <option value="72">At least 3 days</option>
            <option value="168">At least 7 days</option>
          </Select>
        </Field>
        <Field
          label="Preparation time"
          htmlFor="stay-prep"
          error={errors.preparationDays}
          hint="Nights blocked before and after each reservation"
        >
          <Select
            id="stay-prep"
            value={form.preparationDays}
            onChange={event => onChange({ preparationDays: event.target.value })}
          >
            <option value="0">None</option>
            <option value="1">1 night</option>
            <option value="2">2 nights</option>
          </Select>
        </Field>
      </FormGrid>
    </Stack>
  );
}

export function HouseRulesSection({ form, onChange, errors }: SectionProps) {
  return (
    <Stack $gap={6}>
      <div>
        <Toggle
          label="Pets allowed"
          checked={form.petsAllowed}
          onChange={petsAllowed => onChange({ petsAllowed, maxPets: petsAllowed ? '1' : '0' })}
          testId="toggle-pets"
        />
        {form.petsAllowed && (
          <Counter
            label="Maximum pets"
            value={form.maxPets}
            min={1}
            max={10}
            onChange={maxPets => onChange({ maxPets })}
          />
        )}
        <Toggle
          label="Smoking allowed"
          checked={form.smokingAllowed}
          onChange={smokingAllowed => onChange({ smokingAllowed })}
        />
        <Toggle
          label="Events allowed"
          checked={form.eventsAllowed}
          onChange={eventsAllowed => onChange({ eventsAllowed })}
        />
        <Toggle
          label="Suitable for children (2 to 12)"
          checked={form.childrenAllowed}
          onChange={childrenAllowed => onChange({ childrenAllowed })}
        />
        <Toggle
          label="Suitable for infants (under 2)"
          checked={form.infantsAllowed}
          onChange={infantsAllowed => onChange({ infantsAllowed })}
        />
      </div>
      <FormGrid $cols={2}>
        <Field label="Quiet hours from" htmlFor="stay-quiet-from" error={errors.quietHoursFrom}>
          <Input
            id="stay-quiet-from"
            type="time"
            value={form.quietHoursFrom}
            onChange={event => onChange({ quietHoursFrom: event.target.value })}
          />
        </Field>
        <Field label="Quiet hours until" htmlFor="stay-quiet-to" error={errors.quietHoursTo}>
          <Input
            id="stay-quiet-to"
            type="time"
            value={form.quietHoursTo}
            onChange={event => onChange({ quietHoursTo: event.target.value })}
          />
        </Field>
      </FormGrid>
      <Field label="Additional rules (optional)" htmlFor="stay-rules">
        <Textarea
          id="stay-rules"
          rows={3}
          maxLength={2000}
          value={form.additionalRules}
          onChange={event => onChange({ additionalRules: event.target.value })}
        />
      </Field>
    </Stack>
  );
}

export function PriceSection({ form, onChange, errors }: SectionProps) {
  return (
    <Stack $gap={6}>
      <FormGrid $cols={2}>
        <Field
          label="Nightly price"
          htmlFor="stay-weekday-price"
          error={errors.weekdayPrice}
          hint="Sunday to Thursday nights"
          required
        >
          {money({
            id: 'stay-weekday-price',
            value: form.weekdayPrice,
            onChange: weekdayPrice => onChange({ weekdayPrice }),
          })}
        </Field>
        <Field
          label="Weekend price"
          htmlFor="stay-weekend-price"
          error={errors.weekendPrice}
          hint="Friday and Saturday nights. Empty uses the nightly price"
        >
          {money({
            id: 'stay-weekend-price',
            value: form.weekendPrice,
            onChange: weekendPrice => onChange({ weekendPrice }),
          })}
        </Field>
        <Field
          label="Cleaning fee"
          htmlFor="stay-cleaning-fee"
          error={errors.cleaningFee}
          hint="Once per stay"
        >
          {money({
            id: 'stay-cleaning-fee',
            value: form.cleaningFee,
            onChange: cleaningFee => onChange({ cleaningFee }),
          })}
        </Field>
        {/* The rule is the "Pets allowed" switch in House rules: no pets, no pet fee. */}
        <Field
          label="Pet fee"
          htmlFor="stay-pet-fee"
          error={errors.petFee}
          hint={
            form.petsAllowed
              ? 'Once per stay, when pets come'
              : 'Turn on "Pets allowed" in House rules to charge a pet fee'
          }
        >
          {money({
            id: 'stay-pet-fee',
            value: form.petsAllowed ? form.petFee : '0',
            disabled: !form.petsAllowed,
            onChange: petFee => onChange({ petFee }),
          })}
        </Field>
        <Field
          label="Extra guest fee"
          htmlFor="stay-extra-guest-fee"
          error={errors.extraGuestFee}
          hint="Per guest, per night"
        >
          {money({
            id: 'stay-extra-guest-fee',
            value: form.extraGuestFee,
            onChange: extraGuestFee => onChange({ extraGuestFee }),
          })}
        </Field>
        <Field
          label="For each guest after"
          htmlFor="stay-extra-guest-after"
          error={errors.extraGuestAfter}
        >
          <Input
            id="stay-extra-guest-after"
            inputMode="numeric"
            value={form.extraGuestAfter}
            placeholder={form.maxGuests}
            onChange={event => onChange({ extraGuestAfter: event.target.value })}
          />
        </Field>
      </FormGrid>
      <FormGrid $cols={2}>
        <Field
          label="Weekly discount"
          htmlFor="stay-weekly-discount"
          error={errors.weeklyDiscountPercent}
          hint="Stays of 7 nights or more"
        >
          {percent({
            id: 'stay-weekly-discount',
            value: form.weeklyDiscountPercent,
            onChange: weeklyDiscountPercent => onChange({ weeklyDiscountPercent }),
          })}
        </Field>
        <Field
          label="Monthly discount"
          htmlFor="stay-monthly-discount"
          error={errors.monthlyDiscountPercent}
          hint="Stays of 28 nights or more"
        >
          {percent({
            id: 'stay-monthly-discount',
            value: form.monthlyDiscountPercent,
            onChange: monthlyDiscountPercent => onChange({ monthlyDiscountPercent }),
          })}
        </Field>
      </FormGrid>
    </Stack>
  );
}

export function CheckInSection({ form, onChange, errors }: SectionProps) {
  return (
    <Stack $gap={6}>
      <FormGrid $cols={3}>
        <Field label="Check-in from" htmlFor="stay-checkin-from" error={errors.checkInFrom}>
          <Input
            id="stay-checkin-from"
            type="time"
            value={form.checkInFrom}
            onChange={event => onChange({ checkInFrom: event.target.value })}
          />
        </Field>
        <Field
          label="Check-in until"
          htmlFor="stay-checkin-until"
          error={errors.checkInUntil}
          hint="Empty means flexible"
        >
          <Input
            id="stay-checkin-until"
            type="time"
            value={form.checkInUntil}
            onChange={event => onChange({ checkInUntil: event.target.value })}
          />
        </Field>
        <Field label="Checkout by" htmlFor="stay-checkout-by" error={errors.checkOutBy}>
          <Input
            id="stay-checkout-by"
            type="time"
            value={form.checkOutBy}
            onChange={event => onChange({ checkOutBy: event.target.value })}
          />
        </Field>
      </FormGrid>
      <div>
        <Toggle
          label="Self check-in"
          hint="Guests let themselves in"
          checked={form.selfCheckIn}
          onChange={selfCheckIn => onChange({ selfCheckIn })}
        />
      </div>
      <Field label="Check-in method" htmlFor="stay-checkin-method">
        <Select
          id="stay-checkin-method"
          value={form.checkInMethod}
          onChange={event =>
            onChange({ checkInMethod: event.target.value as StayCheckInMethod | '' })
          }
        >
          <option value="">Not set</option>
          {Object.values(StayCheckInMethod).map(method => (
            <option key={method} value={method}>
              {CHECK_IN_METHOD_LABELS[method]}
            </option>
          ))}
        </Select>
      </Field>
      <Field
        label="Check-in instructions"
        htmlFor="stay-checkin-instructions"
        hint="Door code, parking and directions. Only booked guests see this, 48 hours before check-in."
      >
        <Textarea
          id="stay-checkin-instructions"
          rows={4}
          maxLength={2000}
          value={form.checkInInstructions}
          onChange={event => onChange({ checkInInstructions: event.target.value })}
        />
      </Field>
      <FormGrid $cols={2}>
        <Field label="Wi-Fi network" htmlFor="stay-wifi-name">
          <Input
            id="stay-wifi-name"
            maxLength={100}
            value={form.wifiName}
            onChange={event => onChange({ wifiName: event.target.value })}
          />
        </Field>
        <Field label="Wi-Fi password" htmlFor="stay-wifi-password">
          <Input
            id="stay-wifi-password"
            maxLength={100}
            value={form.wifiPassword}
            onChange={event => onChange({ wifiPassword: event.target.value })}
          />
        </Field>
      </FormGrid>
    </Stack>
  );
}

export function CancellationSection({ form, onChange, errors }: SectionProps) {
  const nonRefundable = form.cancellationPolicy === StayCancellationPolicy.NonRefundable;
  return (
    <Stack $gap={4}>
      {Object.values(StayCancellationPolicy).map(policy => (
        <OptionCard
          key={policy}
          type="button"
          $selected={form.cancellationPolicy === policy}
          aria-pressed={form.cancellationPolicy === policy}
          onClick={() =>
            onChange({
              cancellationPolicy: policy,
              nonRefundableDiscountPercent:
                policy === StayCancellationPolicy.NonRefundable &&
                !form.nonRefundableDiscountPercent
                  ? '10'
                  : form.nonRefundableDiscountPercent,
            })
          }
          data-testid={`policy-${policy}`}
        >
          <OptionTitle>{CANCELLATION_POLICY_LABELS[policy].title}</OptionTitle>
          <OptionText>{CANCELLATION_POLICY_LABELS[policy].description}</OptionText>
        </OptionCard>
      ))}
      {nonRefundable && (
        <Field
          label="Non-refundable discount"
          htmlFor="stay-nr-discount"
          error={errors.nonRefundableDiscountPercent}
          hint="At least 10%"
        >
          {percent({
            id: 'stay-nr-discount',
            value: form.nonRefundableDiscountPercent,
            onChange: nonRefundableDiscountPercent => onChange({ nonRefundableDiscountPercent }),
          })}
        </Field>
      )}
    </Stack>
  );
}

/** The editor for one saved section. */
export function StaySectionFields(props: SectionProps & { section: StaySectionId }) {
  switch (props.section) {
    case 'basics':
      return <BasicsSection {...props} />;
    case 'amenities':
      return <AmenitiesSection {...props} />;
    case 'title':
      return <TitleSection {...props} />;
    case 'description':
      return <DescriptionSection {...props} />;
    case 'booking':
      return <BookingSection {...props} />;
    case 'houseRules':
      return <HouseRulesSection {...props} />;
    case 'price':
      return <PriceSection {...props} />;
    case 'checkIn':
      return <CheckInSection {...props} />;
    case 'cancellation':
      return <CancellationSection {...props} />;
    default:
      return null;
  }
}
